import { useEffect, useRef, useState } from "react";
import SidebarAdmin from "./sidebar-admin";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../config";

interface Service {
  id: number;
  title?: string;
  name?: string;
  price: number;
}

interface Promotion {
  id: number;
  title: string;
  description: string;
  service_type: string;
  service_id: number;
  original_price: number;
  promotional_price: number;
  start_date: string;
  end_date: string;
  max_uses: number | null;
  promo_code: string | null;
  terms_conditions: string | null;
  is_active: boolean;
  service_details?: {
    id: number;
    name: string;
    price: number;
  };
}

const EditPromotion = () => {
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedServiceType, setSelectedServiceType] = useState<string>("");
  const [selectedServiceId, setSelectedServiceId] = useState<number | "">("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [originalPrice, setOriginalPrice] = useState("");
  const [promotionalPrice, setPromotionalPrice] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [termsConditions, setTermsConditions] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  
  const navigate = useNavigate();
  const { id } = useParams();

  // Charger la promotion existante
  useEffect(() => {
    const fetchPromotion = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        const response = await axios.get(`${API_URL}/promotions/${id}`);
        const promotion: Promotion = response.data.data;

        // Remplir les champs avec les données existantes
        setTitle(promotion.title);
        setDescription(promotion.description || "");
        setSelectedServiceType(promotion.service_type);
        setSelectedServiceId(promotion.service_id);
        setOriginalPrice(promotion.original_price.toString());
        setPromotionalPrice(promotion.promotional_price.toString());
        setStartDate(promotion.start_date.split('T')[0]);
        setEndDate(promotion.end_date.split('T')[0]);
        setMaxUses(promotion.max_uses?.toString() || "");
        setPromoCode(promotion.promo_code || "");
        setTermsConditions(promotion.terms_conditions || "");
        setIsActive(promotion.is_active);

        setIsLoading(false);
      } catch (error) {
        console.error("Erreur lors du chargement de la promotion:", error);
        toast.error("Error loading promotion");
        setIsLoading(false);
      }
    };

    fetchPromotion();
  }, [id]);

  // Charger les services selon le type sélectionné
  useEffect(() => {
    const fetchServices = async () => {
      if (!selectedServiceType) {
        setServices([]);
        return;
      }

      try {
        let endpoint = "";
        switch (selectedServiceType) {
          case "trip":
            endpoint = `${API_URL}/trips`;
            break;
          case "visa_pack":
            endpoint = `${API_URL}/visa-packs`;
            break;
          case "car_rental":
            endpoint = `${API_URL}/cars`;
            break;
          default:
            return;
        }

        const response = await axios.get(endpoint);
        const servicesData = response.data.data?.data || response.data.data || response.data;
        setServices(Array.isArray(servicesData) ? servicesData : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des services:", error);
      }
    };

    fetchServices();
  }, [selectedServiceType]);

  // Mettre à jour le prix original quand un service est sélectionné
  useEffect(() => {
    if (selectedServiceId && services.length > 0) {
      const selectedService = services.find(service => service.id === selectedServiceId);
      if (selectedService) {
        setOriginalPrice(selectedService.price.toString());
      }
    }
  }, [selectedServiceId, services]);

  const handleUpdate = async () => {
    try {
      const promotionData = {
        title,
        description,
        original_price: parseFloat(originalPrice),
        promotional_price: parseFloat(promotionalPrice),
        start_date: startDate,
        end_date: endDate,
        max_uses: maxUses ? parseInt(maxUses) : null,
        promo_code: promoCode || null,
        terms_conditions: termsConditions || null,
        is_active: isActive
      };

      const response = await axios.put(`${API_URL}/promotions/${id}`, promotionData);
      
      toast.success("Promotion updated successfully!");
      navigate("/admin/list-promotion");
    } catch (error: any) {
      console.error("Erreur lors de la mise à jour de la promotion:", error.response?.data || error);
      const errorMessage = error.response?.data?.message || "Error updating promotion";
      toast.error(errorMessage);
    }
  };

  const handleToggleStatus = async () => {
    try {
      const response = await axios.put(`${API_URL}/promotions/${id}/toggle-status`);
      setIsActive(!isActive);
      toast.success(`Promotion ${!isActive ? 'activated' : 'deactivated'} successfully!`);
    } catch (error: any) {
      console.error("Erreur lors du changement de statut:", error.response?.data || error);
      const errorMessage = error.response?.data?.message || "Error updating promotion status";
      toast.error(errorMessage);
    }
  };

  // Calculer le pourcentage de réduction
  const calculateDiscount = () => {
    if (!originalPrice || !promotionalPrice) return 0;
    const original = parseFloat(originalPrice);
    const promo = parseFloat(promotionalPrice);
    if (original === 0) return 0;
    return ((original - promo) / original * 100).toFixed(2);
  };

  if (isLoading) {
    return (
      <div className="dashboard-layout">
        <aside className="dashboard-sidebar">
          <SidebarAdmin />
        </aside>
        <div className="dashboard-main">
          <div className="d-flex justify-center align-center py-50">
            <div className="text-16">Loading promotion...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-layout">
        <aside className="dashboard-sidebar">
          <SidebarAdmin />
        </aside>

        <div className="dashboard-main">
          <header className="dashboard-header">
            <div className="header-content">
              <button className="menu-toggle">
                <i className="icon-menu-2 text-20"></i>
              </button>
            </div>
          </header>

          <main className="dashboard-content">
            <div className="dashboard__content bg-light-2">
              {/* Header amélioré */}
              <div className="row y-gap-20 justify-between items-center pb-10 lg:pb-30 md:pb-20">
                <div className="col-12">
                  <div className="d-flex justify-between items-center">
                    <div>
                      <h1 className="text-24 fw-600" style={{ color: '#1e293b' }}>Edit Promotion</h1>
                      <p className="text-14 mt-5" style={{ color: '#64748b' }}>
                        Update promotional offer details
                      </p>
                    </div>
                    <div className="d-flex gap-2 align-center">
                      <span className="text-14 fw-500" style={{ color: isActive ? '#059669' : '#dc2626' }}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        type="button"
                        onClick={handleToggleStatus}
                        className={`px-3 py-2 text-white rounded-4 text-12 ${
                          isActive ? 'bg-red-500' : 'bg-green-500'
                        }`}
                        style={{
                          border: "none",
                          cursor: "pointer",
                          fontWeight: '500'
                        }}
                      >
                        {isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="py-30 px-30 rounded-4 bg-white shadow-3 mb-3">
                <div className="col-xl-12">
                  <div className="row x-gap-20 y-gap-20">
                    {/* Service Type (Read-only) */}
                    <div className="col-md-6 col-12">
                      <label className="text-14 fw-500 text-dark-1 mb-8">Service Type</label>
                      <div className="form-input">
                        <select
                          value={selectedServiceType}
                          disabled
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: '#f1f5f9',
                            cursor: 'not-allowed',
                            color: '#64748b'
                          }}
                        >
                          <option value="">-- Service Type --</option>
                          <option value="trip">Trip</option>
                          <option value="visa_pack">Visa Pack</option>
                          <option value="car_rental">Car Rental</option>
                        </select>
                      </div>
                      <p className="text-12 text-gray-500 mt-1">Service type cannot be changed</p>
                    </div>

                    {/* Service Selection (Read-only) */}
                    <div className="col-md-6 col-12">
                      <label className="text-14 fw-500 text-dark-1 mb-8">Service</label>
                      <div className="form-input">
                        <select
                          value={selectedServiceId}
                          disabled
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: '#f1f5f9',
                            cursor: 'not-allowed',
                            color: '#64748b'
                          }}
                        >
                          <option value="">-- Service --</option>
                          {services.map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.title || service.name} - ${service.price}
                            </option>
                          ))}
                        </select>
                      </div>
                      <p className="text-12 text-gray-500 mt-1">Service cannot be changed</p>
                    </div>

                    {/* Title */}
                    <div className="col-12">
                      <label className="text-14 fw-500 text-dark-1 mb-8">Promotion Title *</label>
                      <div className="form-input">
                        <input 
                          type="text" 
                          placeholder="Enter promotion title"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: '#f8fafc'
                          }}
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div className="col-12">
                      <label className="text-14 fw-500 text-dark-1 mb-8">Description</label>
                      <div className="form-input">
                        <textarea
                          placeholder="Enter promotion description"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: '#f8fafc',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>

                    {/* Pricing */}
                    <div className="col-md-6 col-12">
                      <label className="text-14 fw-500 text-dark-1 mb-8">Original Price *</label>
                      <div className="form-input">
                        <input 
                          type="number" 
                          step="0.01"
                          placeholder="0.00"
                          value={originalPrice}
                          onChange={(e) => setOriginalPrice(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: '#f8fafc'
                          }}
                        />
                      </div>
                    </div>

                    <div className="col-md-6 col-12">
                      <label className="text-14 fw-500 text-dark-1 mb-8">Promotional Price *</label>
                      <div className="form-input">
                        <input 
                          type="number" 
                          step="0.01"
                          placeholder="0.00"
                          value={promotionalPrice}
                          onChange={(e) => setPromotionalPrice(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: '#f8fafc'
                          }}
                        />
                      </div>
                    </div>

                    {/* Discount Display */}
                    {originalPrice && promotionalPrice && (
                      <div className="col-12">
                        <div className="bg-green-50 p-3 rounded-4">
                          <div className="text-14 fw-500 text-green-800">
                            Discount: {calculateDiscount()}% off
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Dates */}
                    <div className="col-md-6 col-12">
                      <label className="text-14 fw-500 text-dark-1 mb-8">Start Date *</label>
                      <div className="form-input">
                        <input 
                          type="date" 
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: '#f8fafc'
                          }}
                        />
                      </div>
                    </div>

                    <div className="col-md-6 col-12">
                      <label className="text-14 fw-500 text-dark-1 mb-8">End Date *</label>
                      <div className="form-input">
                        <input 
                          type="date" 
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          min={startDate}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: '#f8fafc'
                          }}
                        />
                      </div>
                    </div>

                    {/* Additional Options */}
                    <div className="col-md-6 col-12">
                      <label className="text-14 fw-500 text-dark-1 mb-8">Max Uses (Optional)</label>
                      <div className="form-input">
                        <input 
                          type="number" 
                          placeholder="Unlimited if empty"
                          value={maxUses}
                          onChange={(e) => setMaxUses(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: '#f8fafc'
                          }}
                        />
                      </div>
                    </div>

                    <div className="col-md-6 col-12">
                      <label className="text-14 fw-500 text-dark-1 mb-8">Promo Code (Optional)</label>
                      <div className="form-input">
                        <input 
                          type="text" 
                          placeholder="Auto-generated if empty"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: '#f8fafc'
                          }}
                        />
                      </div>
                    </div>

                    {/* Terms & Conditions */}
                    <div className="col-12">
                      <label className="text-14 fw-500 text-dark-1 mb-8">Terms & Conditions</label>
                      <div className="form-input">
                        <textarea
                          placeholder="Enter terms and conditions for this promotion"
                          value={termsConditions}
                          onChange={(e) => setTermsConditions(e.target.value)}
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            backgroundColor: '#f8fafc',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="py-30 px-30 mb-3 rounded-4 bg-white shadow-3">
                <div className="d-flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/admin/list-promotion")}
                    className="px-20 py-10 text-white rounded-4"
                    style={{
                      backgroundColor: "#6b7280",
                      border: "none",
                      cursor: "pointer",
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    onClick={handleUpdate}
                    className="px-20 py-10 bg-blue-1 text-white rounded-4"
                    style={{
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Update Promotion
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default EditPromotion;